import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Input,
  Button,
  Alert,
  Container,
  Label,
  Spinner,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { AvForm, AvField } from "availity-reactstrap-validation";
import { getRecords } from "../../services/api.service";
import { toast } from "react-toastify";
import logodark from "../../assets/images/logo-dark.png";
import logolight from "../../assets/images/logo-light.png";
import { Link } from "react-router-dom";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isAlertShown, setIsAlertShown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [accesMenuData, setAccesMenuData] = useState([]);
  const [menuData, setMenuData] = useState([]);

  const navigate = useNavigate();

  const fetchMenuData = async () => {
    const payload = {
      modelName: "menu_masters",
      whereCondition: { is_active: true },
      pagination: { page: 1, pageSize: 1000000 },
    };

    try {
      const { code, data } = await getRecords(payload, "/getRecords");
      if (code === 1) {
        const indexedData = data.map((item, index) => ({
          ...item,
          index: index + 1,
        }));
        setMenuData(indexedData);
        return indexedData;
      }
    } catch (error) {
      console.error("Error fetching menu data:", error);
    }
    return [];
  };

  const fetchAccessMenuData = async (userId) => {
    const payload = {
      modelName: "user_access",
      whereCondition: {
        user_id: userId,
        access: true,
        is_active: true,
      },
      pagination: { page: 1, pageSize: 1000000 },
    };

    try {
      const { code, data } = await getRecords(payload, "/getRecords");
      if (code === 1) {
        setAccesMenuData(data);
        return data;
      }
    } catch (error) {
      console.error("Error fetching access menu data:", error);
    }
    return [];
  };

  const filterMenus = (menuData, accessMenuData) => {
    const allowedIds = accessMenuData.map((item) => item.menu_id);
    return menuData.filter((menu) => allowedIds.includes(menu.id));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        password,
        modelName: "user_masters",
        whereCondition: { email_id: username },
      };

      const response = await getRecords(payload, "/auth/login");

      if (response.code === 1) {
        const user = response.data.user;
        const token = response.data.token;
        const userId = user.id;

        localStorage.setItem("userDetails", JSON.stringify(user));
        localStorage.setItem("accessToken", JSON.stringify(token));

        try {
          const projectPayload = {
            modelName: "project_access",
            whereCondition: { user_id: userId },
            relations: [
              {
                module: "projects",
                moduleAs: "projectDetails",
                required: true,
              },
            ],
          };

          const projectResponse = await getRecords(
            projectPayload,
            "/UserWiseProjectListing"
          );
          localStorage.setItem(
            "projectDetails",
            JSON.stringify(projectResponse.data)
          );
        } catch (error) {
          console.error("Error fetching project details:", error);
          toast.error("Failed to load project details. Please try again.");
        }

        const [menuData, accessMenuData] = await Promise.all([
          fetchMenuData(),
          fetchAccessMenuData(userId),
        ]);

        const filteredMenus = filterMenus(menuData, accessMenuData);
        const targetMenuUrl = (() => {
          const dashboardMenu = filteredMenus.find(
            (menu) => menu.menu_url === "/dashboard"
          );
          if (dashboardMenu) return dashboardMenu.menu_url;

          const lastValidMenu = [...filteredMenus]
            .reverse()
            .find((menu) => menu.menu_url && menu.menu_url.trim() !== "");
          return lastValidMenu?.menu_url;
        })();
        navigate(targetMenuUrl);

        if (targetMenuUrl) {
          toast.success(response.message);
        } else {
          toast.error("No menu access provided to the user");
        }
      } else if (response.code === 0 && !isAlertShown) {
        setIsAlertShown(true);
        toast.error(response.message, {
          onClose: () => setIsAlertShown(false),
        });
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error("An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  return (
    <React.Fragment>
      <div>
        <Container fluid className="p-0 bg-white">
          <Row className="g-0">
            <Col lg={4}>
              <div className="authentication-page-content p-4 d-flex align-items-center min-vh-100">
                <div className="w-100">
                  <Row className="justify-content-center">
                    <Col lg={9}>
                      <div>
                        <div className="text-center">
                          <div>
                            <img
                              src={
                                "https://www.bermad.com/app/uploads/Bermad-logo-new.png"
                              }
                              alt="Logo"
                              height="100"
                              className="auth-logo logo-dark mx-auto"
                            />
                            <img
                              src={
                                "https://www.bermad.com/app/uploads/Bermad-logo-new.png"
                              }
                              alt="Logo"
                              height="100"
                              className="auth-logo logo-light mx-auto"
                            />
                          </div>
                        </div>

                        <div className="p-2 mt-5">
                          <AvForm
                            className="form-horizontal"
                            onValidSubmit={handleSubmit}
                          >
                            <div className="auth-form-group-custom mb-4">
                              <i className="ri-user-2-line auti-custom-input-icon"></i>
                              <Label htmlFor="username">Email</Label>
                              <AvField
                                name="username"
                                value={username}
                                type="text"
                                className="form-control"
                                id="username"
                                placeholder="Enter username"
                                onChange={(e) => setUsername(e.target.value)}
                                validate={{
                                  required: {
                                    value: true,
                                    errorMessage: "Username is required",
                                  },
                                  pattern: {
                                    value:
                                      "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$",
                                    errorMessage:
                                      "Please enter a valid email address",
                                  },
                                }}
                              />
                            </div>

                            <div className="auth-form-group-custom mb-4">
                              <i className="ri-lock-2-line auti-custom-input-icon"></i>
                              <Label htmlFor="userpassword">Password</Label>
                              <AvField
                                name="password"
                                value={password}
                                type={passwordVisible ? "text" : "password"}
                                className="form-control"
                                id="userpassword"
                                placeholder="Enter password"
                                onChange={(e) => setPassword(e.target.value)}
                                validate={{
                                  required: {
                                    value: true,
                                    errorMessage: "Password is required",
                                  },
                                }}
                              />
                              <button
                                type="button"
                                onClick={togglePasswordVisibility}
                                style={{
                                  position: "absolute",
                                  top: "54%",
                                  right: "10px",
                                  transform: "translateY(-50%)",
                                  background: "transparent",
                                  border: "none",
                                  cursor: "pointer",
                                }}
                              >
                                {passwordVisible ? (
                                  <i
                                    className="ri-eye-line"
                                    style={{ fontSize: "1.2em" }}
                                  ></i>
                                ) : (
                                  <i
                                    className="ri-eye-off-line"
                                    style={{ fontSize: "1.2em" }}
                                  ></i>
                                )}
                              </button>
                            </div>

                            <div className="mt-4 text-center">
                              <Button
                                color="primary"
                                className="w-md waves-effect waves-light"
                                type="submit"
                                disabled={loading}
                                style={{ background: "#006eb9" }}
                              >
                                {loading ? "loading..." : "Log In"}
                              </Button>
                            </div>

                            <div className="mt-4 text-center">
                              <Link
                                to="/forgot-password"
                                className="text-muted"
                              >
                                <i className="mdi mdi-lock me-1"></i> Forgot
                                your password?
                              </Link>
                            </div>
                          </AvForm>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
            </Col>
            <Col lg={8}>
              <div className="bermad-wallpaper"></div>
            </Col>
          </Row>
        </Container>

        {loading && (
          <div className="loading-overlay">
            <Spinner size="lg" color="primary" />
          </div>
        )}
      </div>

      {/* Add styling for the loading overlay */}
      <style jsx>{`
        .loading-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 9999;
        }
      `}</style>
    </React.Fragment>
  );
};

export default Login;
